import React from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";

export interface Driver {
  field: string;
  value: number;
}

interface DirectionalDriversProps {
  data?: {
    drivers?: Driver[];
    methodologyNotes?: string;
  } | null;
  loading?: boolean;
}

const DirectionalDrivers: React.FC<DirectionalDriversProps> = ({
  data,
  loading = false,
}) => {
  if (loading) {
    return (
      <div className="w-full p-4 space-y-4 border rounded-lg bg-white dark:bg-gray-800 dark:border-gray-700">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
          <div className="space-y-2">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="h-4 bg-gray-200 dark:bg-gray-700 rounded"
              ></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const drivers = Array.isArray(data?.drivers) ? data.drivers : [];
  const methodologyNotes = data?.methodologyNotes || "";

  // Sort by absolute value for both arrays
  const sortedData = [...drivers].sort(
    (a, b) => Math.abs(b.value) - Math.abs(a.value),
  );
  const increasingFactors = sortedData.filter((d) => d.value > 0);
  const decreasingFactors = sortedData.filter((d) => d.value < 0);

  const maxBarWidth = 100; // percentage
  const maxInfluence = Math.max(0, ...drivers.map((d) => Math.abs(d.value)));

  const getBarWidth = (value: number) => {
    return maxInfluence > 0
      ? (Math.abs(value) / maxInfluence) * maxBarWidth
      : 0;
  };

  const formatImpact = (value: number) => {
    return `${(Math.abs(value) * 100).toFixed(2)}%`;
  };

  return (
    <div className="w-full p-6 space-y-6 border rounded-lg bg-white dark:bg-gray-800 dark:border-gray-700">
      <div className="flex">
        <div className="text-xl font-semibold mb-6">
          Directional Drivers of Attrition
        </div>
        {methodologyNotes && (
          <div className="inline-block ml-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="p-2.5 -m-2.5 inline-flex items-center justify-center cursor-pointer">
                    <Info className="text-primary h-5 w-5 flex-shrink-0" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="w-[300px] p-4">
                  <h4 className="font-semibold mb-2">Directional Drivers</h4>
                  <p className="text-xs">{methodologyNotes}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
      </div>
      {drivers.length === 0 ? (
        <p className="text-sm text-gray-500">
          No directional driver data available.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h3 className="text-primary font-semibold mb-4">
              Factors that decrease attrition
            </h3>
            <div className="space-y-2 border-r border-gray-200 dark:border-gray-700">
              {decreasingFactors.map((driver, index) => (
                <div key={index} className="flex items-center">
                  <div className="flex-1 text-sm truncate">{driver.field}</div>
                  <div className="w-1/2 flex justify-end">
                    <TooltipProvider>
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                          <div
                            className="h-4 bg-primary rounded-l cursor-pointer"
                            style={{
                              width: `${getBarWidth(driver.value)}%`,
                            }}
                          />
                        </TooltipTrigger>
                        <TooltipContent side="top" sideOffset={5}>
                          <div className="flex flex-col gap-2">
                            <div className="text-base font-medium">
                              {driver.field}
                            </div>
                            <div className="h-px bg-gray-200 dark:bg-gray-700 -mx-2" />
                            <div className="text-sm">
                              <span className="text-gray-500">
                                Impact on attrition:{" "}
                              </span>
                              <span className="font-medium text-primary">
                                -{formatImpact(driver.value)}
                              </span>
                            </div>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-secondary font-semibold mb-4 text-right">
              Factors that increase attrition
            </h3>
            <div className="space-y-2 border-l border-gray-300 dark:border-gray-700">
              {increasingFactors.map((driver, index) => (
                <div key={index} className="flex items-center">
                  <div className="w-1/2 flex justify-start">
                    <TooltipProvider>
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                          <div
                            className="h-4 bg-secondary rounded-r cursor-pointer"
                            style={{ width: `${getBarWidth(driver.value)}%` }}
                          />
                        </TooltipTrigger>
                        <TooltipContent side="top" sideOffset={5}>
                          <div className="flex flex-col gap-2">
                            <div className="text-base font-medium">
                              {driver.field}
                            </div>
                            <div className="h-px bg-gray-200 dark:bg-gray-700 -mx-2" />
                            <div className="text-sm">
                              <span className="text-gray-500">
                                Impact on attrition:{" "}
                              </span>
                              <span className="font-medium text-secondary">
                                +{formatImpact(driver.value)}
                              </span>
                            </div>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <div className="flex-1 text-sm truncate text-right">
                    {driver.field}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DirectionalDrivers;
